import React, { useEffect, useState } from 'react'
import PasswordField from '../shared/atom/PasswordField'
import ValidationHint from '../shared/atom/MpValidationHint'
import { Button, Box, TextField } from '../shared/MaterialExports'
import { signUpWithEmail } from '../../services/AuthenticationService'
import { useLocation, useNavigate } from 'react-router-dom'
import { MpTypography } from '../shared'
import AnalyticsManager from '../../services/AnalyticsManager'
import Cookies from 'universal-cookie'
import MpTheme from 'shared/config/MpTheme'
import Stack from '@mui/material/Stack/Stack'
import useMediaQuery from '@mui/material/useMediaQuery'
import { MpReTheme, fonts, theme } from '../../theme/OnboardingTheme'
import { Link } from '@mui/material'
import {logFirebaseEvent} from "../../utils/firebaseConfig";
import { isValidEmail } from '../../utils/helpers'
import Loader from 'shared/components/atoms/Loader'
import { logCustomMetapixelEvent } from '../../utils/MetapixelManager'

const minPasswordLength = 10

const SignUpEmail = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const from = (location?.state as any)?.from || '/'
  const [email, setEmail] = useState<string>('')
  const [password, setPassword] = useState<string>('')
  const [confirmPassword, setConfirmPassword] = useState<string>('')
  const [errorText, setErrorText] = useState<string>('')
  const [loading, setLoading] = useState<boolean>(false)

  const [shouldDisplayPasswordInfo, setShouldDisplayPasswordInfo] =
    useState<boolean>(false)
  const [shouldShowMatchingPasswords, setShouldShowMatchingPasswords] =
    useState<boolean>(false)
  const [hasSufficientLength, setHasSufficientLength] = useState<boolean>(false)
  const [hasUpperCase, setHasUpperCase] = useState<boolean>(false)
  const [hasSymbol, setHasSymbol] = useState<boolean>(false)
  const [hasMatchingPassword, setHasMatchingPassword] = useState<boolean>(true)
  const isPasswordValid =
    hasSufficientLength && hasUpperCase && hasSymbol && hasMatchingPassword

  const params = new URLSearchParams(window.location.search);
  const clickID = params.get('irclickid');
  if (clickID) {
    const cookies = new Cookies()
    cookies.set('irclickid', clickID, { path: '/' })
  }
  const mpOfferId = params.get('mpOfferId');
  if (mpOfferId) {
    const cookies = new Cookies()
    cookies.set('mpOfferId', mpOfferId, {
      path: '/',
      maxAge: 60 * 60 * 24 * 365 * 100,
    })
  }
  useEffect(() => {
    setHasSufficientLength(password.length >= minPasswordLength)
    setHasUpperCase(new RegExp(/[A-Z]/).test(password))
    setHasSymbol(new RegExp(/[^A-Za-z0-9]/).test(password))
    setHasMatchingPassword(password === confirmPassword)
  }, [password, confirmPassword])

  const onSignUp = async () => {
    setLoading(true)
    if (!isValidEmail(email)) {
        setErrorText('Please enter a valid email address.');
        setLoading(false);
        return;
      }

    try {
      await signUpWithEmail(email, password)
      await AnalyticsManager.registerSuperProperties({"email":email})
      AnalyticsManager.trackTypedEvent({
        event: 'Sign Up - Web',
        source: "Web App"
      })
      logFirebaseEvent('Sign Up - Web', {"email": email})
      logCustomMetapixelEvent('Sign Up - Web')
      navigate('/otp', {
        state: { actionType: 'SIGNUP_EMAIL', username: email, password, from },
      })
    } catch (error) {
      setLoading(false)
      if (error instanceof Error) {
        switch (error.name) {
          case 'UsernameExistsException':
            setErrorText(
              'A username with that email already exists. Try logging in by clicking "I Already Have An Account"',
            )
            break
          case 'LimitExceededException':
            setErrorText(
              `Unfortunately, mePrism is experiencing increased traffic and is unable to handle your sign up request. Please wait 15 minutes and try again.`,
            )
            AnalyticsManager.error('CognitoLimit', error)
            break
          case 'InvalidParameterException':
             setErrorText(
              'Please enter a valid email or phone number.'
            )
            break
          default:
            AnalyticsManager.error(`Unhandled Authorization error: `, error)
            setErrorText(
              'An error occurred processing your signup request. Please try again later.',
            )
            break
        }
      } else {
        AnalyticsManager.error(`Unhandled Authorization error: `, error)
      }
    }
  }
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const textStyle = {
    ...fonts.Inter.Regular,
    fontSize: '16px',
  }
  return (
    <Stack
      sx={{ maxWidth: MpTheme.layouts.widths.sm, mx: 'auto', width: !isMobile?'100%':'90%' }}
      spacing={5}>
      <TextField
        fullWidth
        value={email}
        onChange={(event) => setEmail(event.target.value.replace(/\s/g, ''))}
        color={'primary'}
        variant={'outlined'}
        autoComplete="email"
        InputLabelProps={{ shrink: true }}
        InputProps={{
          placeholder: 'Email',
        }}
        sx={{
          "& input:-webkit-autofill": {
            WebkitBoxShadow: "0 0 0 1000px #1A1B20 inset !important",
            transition: "background-color 5000s ease-in-out 0s",
          },
        }}
      />
      <PasswordField
        value={password}
        placeholder='Password'
        onChange={(event) => setPassword(event.target.value.replace(/\s/g, ''))}
        onFocus={() => {
          setShouldDisplayPasswordInfo(true)
        }}
        variant={'outlined'}
        sx={{
          "& input:-webkit-autofill": {
            WebkitBoxShadow: "0 0 0 1000px #1A1B20 inset !important",
            transition: "background-color 5000s ease-in-out 0s",
          },
        }}
        autoComplete="new-password"
        InputLabelProps={{ shrink: true }}
      />
      <PasswordField
      placeholder='Confirm Password'
        value={confirmPassword}
        onChange={(event) => setConfirmPassword(event.target.value.replace(/\s/g, ''))}
        onFocus={() => {
          setShouldShowMatchingPasswords(true)
        }}
        variant={'outlined'}
        InputLabelProps={{ shrink: true }}
        autoComplete="new-password"
        sx={{
          "& input:-webkit-autofill": {
            WebkitBoxShadow: "0 0 0 1000px #1A1B20 inset !important",
            transition: "background-color 5000s ease-in-out 0s",
          },
        }}
      />
      <Box>
        {shouldDisplayPasswordInfo && (
          <Box>
            <ValidationHint
              isValid={hasSufficientLength}
              text={`At least ${minPasswordLength} characters`}
            />
            <ValidationHint
              isValid={hasUpperCase}
              text={'Contains at least one upper case letter'}
            />
            <ValidationHint isValid={hasSymbol} text={'Contains a symbol'} />
            {shouldShowMatchingPasswords && (
              <ValidationHint
                isValid={hasMatchingPassword}
                text={'Passwords match'}
              />
            )}
          </Box>
        )}
      </Box>
      {errorText && (
        <Box>
          <MpTypography color={'error'}>{errorText}</MpTypography>
        </Box>
      )}
      <Box>
        <Button
          disabled={!isPasswordValid || loading}
          fullWidth
          onClick={onSignUp}>
          <Box 
            sx={{
              display: 'flex',   
              alignItems: 'center',
              gap: 2,
              justifyContent: 'center',
              whiteSpace: 'nowrap',
              width: '38%',
              '& .MuiCircularProgress-root': {
                color: '#5856d6 !important',
                width: '20px !important',
                height: '20px !important',
              },
              }}
            >
            <Box sx={{ flexGrow: 1, textAlign: 'center' }}>
              Create Account
            </Box>
            <Loader loading={loading} />
          </Box>
        </Button>
      </Box>
      {!isMobile &&(<Box sx={{  maxWidth: MpTheme.layouts.widths.sm,width: '100%',position:'absolute',bottom:!isMobile?'30px':'80px'}}>
              <Stack
                direction={'row'}
                sx={{ alignItems: 'center', mt: 15,}}
                spacing={!isMobile?15:10}
                justifyContent={'space-between'}>
                <Link
                  href={'https://meprism.com/terms-conditions'}
                  sx={{
                    ...textStyle,
                    color: MpReTheme.colors.secondary.default,
                  }}>
                  Terms & Conditions
                </Link>
                <Link
                  href={'https://meprism.com/privacy-policy'}
                  sx={{
                    ...textStyle,
                    color: MpReTheme.colors.secondary.default,
                  }}>
                  Privacy Policy
                </Link>
                <Link
                  href={'https://meprism1.atlassian.net/servicedesk/customer/portal/1/group/1/create/17'}
                  sx={{
                    ...textStyle,
                    color: MpReTheme.colors.secondary.default,
                  }}>
                  Contact Us
                </Link>
              </Stack>
            </Box>)}
    </Stack>
  )
}

export default SignUpEmail
